import { createSelector } from '@reduxjs/toolkit';
import { filter as lodashFilter, map } from 'lodash';

/** Shared so a missing tenant or allow-list returns the same reference every time. */
const NO_FACILITIES = [];

/**
 * POM-5827: auth.fulfillingPharmacies is role-based and can include INACTIVE pharmacies, so
 * selection dropdowns must not offer them. The app-wide pharmacy store (state.pharmacy) is loaded
 * active-only (getFulfillingPharmacies filters isActive = true), so intersecting the two yields the
 * active, allowed pharmacies — keeping the auth shape
 * ({ fulfillingPharmacyId, fulfillingPharmacyName }) so callers need no other change.
 */
/**
 * Every facility picker in Stock Management reads the list below, so its order is settled once
 * here rather than in each of them. Case-insensitive: a raw comparison sorts every capitalised
 * name above every lowercase one, which reads as no order at all.
 */
const compareByFacilityName = (a, b) =>
  String(a?.fulfillingPharmacyName ?? '').localeCompare(
    String(b?.fulfillingPharmacyName ?? ''),
    undefined,
    { sensitivity: 'base' }
  );

export const selectActiveFulfillingPharmacies = (state) => {
  const currentTenantId = state.auth?.currentTenantId;
  const activeFacilities = state.pharmacy?.[currentTenantId] || [];
  const activeIds = new Set(map(activeFacilities, 'fulfillingPharmacyId'));
  const allowed = state.auth?.fulfillingPharmacies || [];
  return lodashFilter(allowed, (fp) => activeIds.has(fp?.fulfillingPharmacyId)).sort(
    compareByFacilityName
  );
};

const compareByName = (a, b) =>
  String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' });

/**
 * The same intersection as above, kept in the PHARMACY store's shape rather than auth's.
 *
 * Both answer "which facilities may this user see". They differ only in which record comes back:
 * auth's carries the id and name, the pharmacy store's also carries state and the rest of the
 * facility, which the inventory screens display. Picking the wrong one shows blank labels.
 *
 * Fails CLOSED on an empty allow-list, unlike selectAccessibleFacilities in the shipment
 * selectors, which returns every tenant facility on the assumption that empty means an admin. An
 * admin on dev4 carries an explicit list, so empty is more likely a user whose access was never
 * set - and handing them the tenant is exactly what POM-6274 is about.
 *
 * Memoised, unlike its neighbour above: the screens reading it include the dashboard's pivot grid,
 * which previously read `pharmacy[currentTenantId]` straight and so held a stable reference across
 * unrelated dispatches. Filtering and sorting on every call would hand it a new array each time and
 * re-render the grid on every action.
 */
export const selectAuthorizedFacilities = createSelector(
  [
    (state) => state.pharmacy?.[state.auth?.currentTenantId] ?? NO_FACILITIES,
    (state) => state.auth?.fulfillingPharmacies ?? NO_FACILITIES,
  ],
  (tenantFacilities, allowedFacilities) => {
    const allowedIds = new Set(map(allowedFacilities, 'fulfillingPharmacyId'));

    return lodashFilter(tenantFacilities, (f) => allowedIds.has(f?.fulfillingPharmacyId)).sort(
      compareByName
    );
  }
);
